import React from "react";
import { Home, FileCheck, Shield, Calculator, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CategoryResources } from "@/components/CategoryResources";
import { SectionHeading } from "@/components/ResourceCard";
import { IMG } from "@/lib/assets";

const SECTIONS = [
  { icon: FileCheck, title: "First-Time Homebuyer & Down-Payment Assistance", desc: "Programs that help with down payments, closing costs, and competitive mortgage rates for eligible first-time buyers." },
  { icon: Shield, title: "Rental Assistance & Tenant Rights", desc: "Eviction prevention resources, rental help, and guidance on your rights as a tenant." },
  { icon: Home, title: "Affordable Housing Directory", desc: "Links to recognized affordable housing programs and HUD-approved housing counselors." },
  { icon: Calculator, title: "Homeownership Readiness", desc: "Education and checklists to prepare financially and practically for homeownership." },
];

const CHECKLIST = [
  "Review your credit report and understand your score",
  "Build a budget and save for down payment and closing costs",
  "Connect with a HUD-approved housing counselor (free)",
  "Get pre-qualified and explore down-payment assistance",
  "Complete a homebuyer education course",
];

export default function Housing() {
  return (
    <main>
      <PageHero overline="Real Estate & Housing Resource Center" image={IMG.housing1}
        title="A clear path to stable, affordable housing"
        subtitle="Affordable housing, rental assistance, homeownership education, and down-payment programs — with links to recognized housing counselors. We do not advertise speculative real estate investments." />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-20">
        <SectionHeading overline="How We Help" title="Housing support, from renting to owning" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SECTIONS.map((s, i) => (
            <div key={i} className="card-hover bg-white rounded-2xl border border-slate-200 p-7 flex gap-5">
              <div className="h-14 w-14 rounded-2xl bg-navy-900 text-gold-light flex items-center justify-center shrink-0"><s.icon className="h-7 w-7" /></div>
              <div>
                <h3 className="font-display text-xl font-bold text-navy-900">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <img src={IMG.housing2} alt="Affordable neighborhood homes" className="rounded-3xl w-full h-[420px] object-cover shadow-xl" />
          <div>
            <SectionHeading overline="Homeownership Readiness" title="Your 5-step readiness checklist" />
            <ul className="space-y-4">
              {CHECKLIST.map((c, i) => (
                <li key={i} className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-4">
                  <span className="h-7 w-7 rounded-full bg-verified/10 text-verified flex items-center justify-center shrink-0 font-bold text-sm">{i + 1}</span>
                  <span className="text-slate-700">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <CategoryResources category="affordable-housing" overline="Verified Programs"
        title="Housing & homeownership opportunities"
        subtitle="Verified housing programs and counseling resources, clearly attributed to their providers."
        categoryName="Housing & Homeownership" />
    </main>
  );
}
